import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = { eur: "€", usd: "$", gbp: "£" };

const SuccessPage = () => {
  const [status, setStatus] = useState("checking");
  const [transaction, setTransaction] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const sessionId = new URLSearchParams(location.search).get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollStatus(sessionId, 0);
    } else {
      setStatus("confirmed");
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pollStatus = async (sid, attempts) => {
    if (attempts >= 6) {
      setStatus("confirmed");
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout-status/${sid}`);
      setTransaction(res.data);
      const { payment_status, status: txStatus } = res.data;
      if (payment_status === "paid" || txStatus === "complete") {
        setStatus("confirmed");
      } else if (txStatus === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => pollStatus(sid, attempts + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(sid, attempts + 1), 2000);
    }
  };

  const currencySymbol = transaction
    ? CURRENCY_SYMBOLS[transaction.currency] || transaction.currency?.toUpperCase()
    : "€";

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-6">
      <div className="text-center max-w-sm w-full">
        {status === "checking" ? (
          <>
            <div className="mb-6 flex justify-center">
              <Loader2 className="animate-spin text-[#D4AF37]" size={40} />
            </div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#A8A29E] mb-3 font-manrope">
              Please wait
            </p>
            <h2 className="font-playfair text-3xl text-[#1C1917] mb-3 tracking-tight">
              Verifying Payment
            </h2>
            <p className="text-sm text-[#A8A29E] font-manrope">
              Confirming your booking details…
            </p>
          </>
        ) : status === "expired" ? (
          <>
            <h1 className="font-playfair text-3xl text-[#1C1917] mb-4 tracking-tight">
              Session Expired
            </h1>
            <p className="text-sm text-[#A8A29E] mb-8 font-manrope">
              Your payment session has expired. Please try again.
            </p>
            <button
              onClick={() => navigate("/")}
              className="h-12 px-8 bg-[#1C1917] text-[#D4AF37] text-[10px] tracking-widest uppercase font-semibold font-manrope hover:bg-[#292524] transition-colors rounded-sm"
            >
              Try Again
            </button>
          </>
        ) : (
          <>
            <div className="mb-6 flex justify-center">
              <div className="w-16 h-16 rounded-full border-2 border-[#D4AF37] flex items-center justify-center">
                <CheckCircle className="text-[#D4AF37]" size={30} />
              </div>
            </div>
            <p
              data-testid="payment-confirmed-label"
              className="text-[10px] tracking-[0.3em] uppercase text-[#A8A29E] mb-3 font-manrope"
            >
              Booking Confirmed
            </p>
            <h1 className="font-playfair text-4xl text-[#1C1917] mb-4 tracking-tight">
              Payment Received
            </h1>
            <p className="text-sm text-[#A8A29E] mb-6 font-manrope leading-relaxed">
              Goldline Management will confirm your booking shortly.
            </p>

            {transaction && (
              <div
                data-testid="payment-summary-card"
                className="bg-white border border-[#E7E5E4] rounded-sm p-5 text-left mb-8"
              >
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#A8A29E] font-manrope">Property</span>
                    <span className="text-[#1C1917] font-manrope font-medium">
                      {transaction.listing_name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A8A29E] font-manrope">Check-in</span>
                    <span className="text-[#1C1917] font-manrope">{transaction.check_in}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A8A29E] font-manrope">Check-out</span>
                    <span className="text-[#1C1917] font-manrope">{transaction.check_out}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A8A29E] font-manrope">Nights</span>
                    <span className="text-[#1C1917] font-manrope">{transaction.nights}</span>
                  </div>
                  <div className="border-t border-dashed border-[#E7E5E4] pt-2.5 flex justify-between items-center">
                    <span className="text-[10px] tracking-widest uppercase text-[#A8A29E] font-manrope">
                      Total Paid
                    </span>
                    <span className="font-playfair text-xl font-semibold text-[#1C1917]">
                      {currencySymbol}
                      {transaction.amount?.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <button
              data-testid="back-to-portal-btn"
              onClick={() => navigate("/")}
              className="h-12 px-8 bg-[#1C1917] text-[#D4AF37] text-[10px] tracking-widest uppercase font-semibold font-manrope hover:bg-[#292524] transition-colors rounded-sm"
            >
              Back to Portal
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SuccessPage;
